<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { searchAddressSuggestions, getPlaceDetails, hasAuthError } from '@/services/googleMapsService';
import { getCurrentApiKey } from '@/plugins/googleMapsLoader';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: 'Endereço'
  },
  placeholder: {
    type: String,
    default: 'Digite um endereço'
  },
  required: {
    type: Boolean,
    default: false
  },
  variant: {
    type: String,
    default: 'outlined'
  },
  clearable: {
    type: Boolean,
    default: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  density: {
    type: String,
    default: 'comfortable'
  }
});

const emit = defineEmits(['update:modelValue', 'place-selected', 'input', 'clear', 'api-error', 'copy-text']);

const searchInput = ref(props.modelValue);
const suggestions = ref([]);
const loading = ref(false);
const menuOpen = ref(false);
const selectedPlace = ref(null);
const debounceTimeout = ref(null);
const inputField = ref(null); // Referência ao campo de input
const suggestionsDropdown = ref(null); // Referência ao dropdown de sugestões

// Estado de erro da API
const mapsError = ref(hasAuthError());
const errorApiKey = ref('');

// Computed property para mostrar o menu apenas quando houver sugestões
const showMenu = computed(() => menuOpen.value && suggestions.value.length > 0);

// Nossa implementação própria de debounce
const debounce = (fn, delay) => {
  return (...args) => {
    if (debounceTimeout.value) clearTimeout(debounceTimeout.value);
    debounceTimeout.value = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

const debouncedSearch = debounce(async (query) => {
  
  if (!query || query.length < 3) {
    suggestions.value = [];
    menuOpen.value = false;
    return;
  }

  loading.value = true;
  try {
    const results = await searchAddressSuggestions(query);
    suggestions.value = results;
    menuOpen.value = results.length > 0;
  } catch (error) {
    loading.value = false;
    suggestions.value = [];
  } finally {
    loading.value = false;
  }
}, 300);

// Função para atualizar o input e emitir eventos
const updateInput = (value) => {
  searchInput.value = value;
  emit('update:modelValue', value);
  emit('input', value);
};

// Função para lidar com a seleção de um endereço
const selectAddress = async (suggestion) => {
  menuOpen.value = false;
  updateInput(suggestion.description);
  
  loading.value = true;
  try {
    const details = await getPlaceDetails(suggestion.id);
    selectedPlace.value = details;
    emit('place-selected', details);
  } catch(error) {
  } finally {
    loading.value = false;
  }
};

// Função para limpar o input
const clearInput = () => {
  updateInput('');
  selectedPlace.value = null;
  suggestions.value = [];
  menuOpen.value = false;
  emit('clear');
};

// Adicionar método para ajudar na depuração
const onInputChange = () => {
  if (searchInput.value) {
    debouncedSearch(searchInput.value);
  } else {
    suggestions.value = [];
    menuOpen.value = false;
  }
};

// Substituir o watch por uma chamada mais direta
watch(searchInput, (newVal, oldVal) => {
  if (newVal !== props.modelValue) {
    emit('update:modelValue', newVal);
  }
  
  // Não chamamos debouncedSearch aqui, pois faremos isso diretamente no evento @input
});

// Observar alterações no modelValue (caso seja alterado externamente)
watch(() => props.modelValue, (newVal) => {
  if (newVal !== searchInput.value) {
    searchInput.value = newVal;
  }
});

// Cancelar debounce quando o componente for desmontado
onUnmounted(() => {
  if (debounceTimeout.value) {
    clearTimeout(debounceTimeout.value);
  }
});

// Adicionar onMounted para garantir que está tudo inicializado corretamente
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('google-maps-auth-error', handleApiAuthError);
  
  window.addEventListener('resize', positionDropdown);
  window.addEventListener('scroll', positionDropdown, true);
  
  // Check if there's an authentication error on mount
  if (hasAuthError()) {
    mapsError.value = true;
    errorApiKey.value = getCurrentApiKey() || 'desconhecida';
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('google-maps-auth-error', handleApiAuthError);
  window.removeEventListener('resize', positionDropdown);
  window.removeEventListener('scroll', positionDropdown, true);
});

// Adicionamos um evento global para fechar o menu quando clicar fora
const handleClickOutside = (event) => {
  const autocompleteEl = document.querySelector('.address-autocomplete-container');
  const dropdownEl = document.getElementById(dropdownPortalId);
  
  if (
    showMenu.value && 
    ((autocompleteEl && !autocompleteEl.contains(event.target)) && 
     (dropdownEl && !dropdownEl.contains(event.target)))
  ) {
    menuOpen.value = false;
  }
};

// Portal para o dropdown
const dropdownPortalId = `address-autocomplete-dropdown-${Date.now()}`;
let dropdownPortalElement = null;

// Posicionar o dropdown de sugestões de forma absoluta em relação ao viewport
const positionDropdown = () => {
  if (!showMenu.value || !inputField.value || !dropdownPortalElement) return;

  // Pegar o retângulo do campo de entrada
  const inputRect = inputField.value.$el.getBoundingClientRect();
  
  // Aplicar posição ao dropdown
  dropdownPortalElement.style.position = 'fixed';
  dropdownPortalElement.style.width = `${inputRect.width}px`;
  dropdownPortalElement.style.left = `${inputRect.left}px`;
  dropdownPortalElement.style.top = `${inputRect.bottom + window.scrollY}px`;
  dropdownPortalElement.style.maxHeight = '300px';
  dropdownPortalElement.style.overflowY = 'auto';
  dropdownPortalElement.style.zIndex = '999999';
  dropdownPortalElement.style.backgroundColor = 'white';
  dropdownPortalElement.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
  dropdownPortalElement.style.borderRadius = '4px';
};

// Função para renderizar as sugestões no portal
const renderSuggestions = () => {
  if (!dropdownPortalElement) return;
  
  if (showMenu.value) {
    // Limpar conteúdo anterior
    dropdownPortalElement.innerHTML = '';
    
    // Criar card de sugestões
    const card = document.createElement('div');
    card.className = 'suggestions-card';
    
    // Criar lista de sugestões
    const list = document.createElement('div');
    list.className = 'suggestions-list';
    
    // Adicionar cada sugestão
    suggestions.value.forEach(suggestion => {
      const item = document.createElement('div');
      item.className = 'suggestion-item';
      
      const title = document.createElement('div');
      title.className = 'suggestion-title';
      title.textContent = suggestion.mainText;
      
      const subtitle = document.createElement('div');
      subtitle.className = 'suggestion-subtitle';
      subtitle.textContent = suggestion.secondaryText;
      
      item.appendChild(title);
      item.appendChild(subtitle);
      
      // Adicionar evento de clique
      item.addEventListener('click', () => {
        selectAddress(suggestion);
      });
      
      list.appendChild(item);
    });
    
    if (loading.value && suggestions.value.length === 0) {
      const loadingItem = document.createElement('div');
      loadingItem.className = 'suggestion-loading';
      loadingItem.textContent = 'Buscando...';
      list.appendChild(loadingItem);
    }
    
    card.appendChild(list);
    dropdownPortalElement.appendChild(card);
    
    // Mostrar e posicionar o dropdown
    dropdownPortalElement.style.display = 'block';
    positionDropdown();
  } else {
    // Esconder o dropdown
    dropdownPortalElement.style.display = 'none';
  }
};

// Observar quando o menu deve ser mostrado/escondido
watch(showMenu, (newVal) => {
  renderSuggestions();
});

// Observar mudanças nas sugestões
watch(suggestions, () => {
  renderSuggestions();
}, { deep: true });

// Observar mudanças no estado de carregamento
watch(loading, () => {
  renderSuggestions();
});

// Criar e remover o elemento portal
onMounted(() => {
  // Criar elemento portal
  dropdownPortalElement = document.createElement('div');
  dropdownPortalElement.id = dropdownPortalId;
  dropdownPortalElement.style.display = 'none';
  document.body.appendChild(dropdownPortalElement);
  
  // Adicionar estilos globais para as sugestões
  const style = document.createElement('style');
  style.textContent = `
    #${dropdownPortalId} .suggestions-list {
      padding: 8px 0;
    }
    
    #${dropdownPortalId} .suggestion-item {
      padding: 8px 16px;
      cursor: pointer;
    }
    
    #${dropdownPortalId} .suggestion-item:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
    
    #${dropdownPortalId} .suggestion-title {
      font-weight: 500;
      font-size: 0.875rem;
    }
    
    #${dropdownPortalId} .suggestion-subtitle {
      font-size: 0.75rem;
      color: rgba(0, 0, 0, 0.6);
    }
    
    #${dropdownPortalId} .suggestion-loading {
      padding: 8px 16px;
      text-align: center;
      font-style: italic;
      color: rgba(0, 0, 0, 0.6);
    }
  `;
  document.head.appendChild(style);
});

onUnmounted(() => {
  // Remover elemento portal
  if (dropdownPortalElement) {
    document.body.removeChild(dropdownPortalElement);
  }
});

// Função para tratar erros de autenticação da API do Google Maps
const handleApiAuthError = (event) => {
  mapsError.value = true;
  errorApiKey.value = event.detail?.apiKey || getCurrentApiKey() || 'desconhecida';
  
  // Desabilitar loading para não confundir o usuário
  loading.value = false;
  
  // Emitir evento para o componente pai
  emit('api-error', {
    message: 'Erro de autenticação da API Google Maps',
    apiKey: errorApiKey.value 
  });
};

// Função para copiar a chave para o clipboard
const copyApiKey = async () => {
  try {
    await navigator.clipboard.writeText(errorApiKey.value);
    emit('copy-text', { 
      text: 'Chave da API', 
      value: errorApiKey.value 
    });
  } catch (error) {
  }
};
</script>

<template>
  <div class="address-autocomplete-container">
    <v-text-field
      ref="inputField"
      v-model="searchInput"
      :label="label"
      :placeholder="mapsError ? 'Busca indisponível - Erro na API' : placeholder"
      :variant="variant"
      :density="density"
      :clearable="clearable && !mapsError"
      :disabled="disabled || mapsError"
      :required="required"
      :loading="loading && !mapsError"
      @click:clear="clearInput"
      @focus="menuOpen = true"
      @input="onInputChange"
      prepend-inner-icon="mdi-map-marker"
      hide-details="auto"
      autocomplete="off"
      :error="mapsError"
      :error-messages="mapsError ? 'Erro na API do Google Maps. Contate o suporte!' : ''"
    >
      <template v-slot:append>
        <v-progress-circular
          v-if="loading && !mapsError"
          indeterminate
          size="20"
          width="2"
          color="primary"
        ></v-progress-circular>
        <v-tooltip v-if="mapsError" location="bottom">
          <template v-slot:activator="{ props }">
            <v-icon
              color="error"
              v-bind="props"
              icon="mdi-alert-circle"
            ></v-icon>
          </template>
          <span>
            Erro de autenticação na API do Google Maps.<br>
            Chave não autorizada: {{ errorApiKey }}
          </span>
        </v-tooltip>
      </template>
    </v-text-field>
    
    <!-- Alerta de erro da API do Google Maps - versão compacta -->
    <div v-if="mapsError" class="error-compact mt-2 d-flex align-center">
      <v-chip
        color="error"
        variant="tonal"
        class="flex-grow-1"
        prepend-icon="mdi-google"
      >
        <span class="text-caption">Chave API inválida: <strong>{{ errorApiKey }}</strong></span>
      </v-chip>
      <v-btn
        icon="mdi-content-copy"
        density="comfortable"
        size="small"
        variant="text"
        color="error"
        @click="copyApiKey"
      ></v-btn>
      <v-tooltip activator="parent">Copiar chave para relatar ao suporte</v-tooltip>
    </div>
  </div>
</template>

<style scoped>
.address-autocomplete-container {
  position: relative;
  width: 100%;
}

/* Os estilos do dropdown foram transferidos para o estilo global criado dinamicamente */

.error-compact {
  border-radius: 4px;
  overflow: hidden;
}
</style>
